import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { IconButton, Tooltip, useAlert, useDialog } from "@digital-services/ui";

import { OrganizationUserDetails } from "@organization/models/OrganizationUserDetails";
import { useRemoveAdmin } from "@organization/services/use-delete-organization-admins";

import TrashIcon from "@icons/trash.svg?react";

interface Props {
  organizationId: string;
  admins: OrganizationUserDetails[];
  isAllSelected: boolean;
  disabled?: boolean;
  onRemove: () => void;
}

const RemoveOrganizationAdminButton: FunctionComponent<Props> = ({
  organizationId,
  admins,
  isAllSelected,
  disabled,
  onRemove
}) => {
  const { showDialog } = useDialog();
  const { notifySuccess, notifyError } = useAlert();

  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.admins"
  });
  const { t: tCommon } = useTranslation();

  const { remove } = useRemoveAdmin();

  const handleDelete = (): void => {
    if (isAllSelected) {
      handleError();
    } else {
      handleConfirmDelete();
    }
  };

  const handleError = (): void => {
    showDialog({
      variant: "error",
      header: t("delete.alert.title"),
      content: t("delete.alert.message"),
      cancelButton: tCommon("button.cancel.label"),
      onCancel: () => {
        /* Do nothing */
      },
      "data-testid": "delete-one-admin-required"
    });
  };

  const handleConfirmDelete = (): void => {
    showDialog({
      variant: "warning",
      header: t("delete.confirm.title"),
      content: t("delete.confirm.message", { count: admins.length }),
      confirmButton: tCommon("button.confirm.label"),
      onConfirm: async () => {
        await removeUsers().then(() => {
          onRemove();
        });
      },
      cancelButton: tCommon("button.cancel.label"),
      onCancel: () => {
        /* Do nothing */
      },
      "data-testid": "delete-admin"
    });
  };

  const removeUsers = async () => {
    let index = 0;
    for (const admin of admins) {
      if (index < admins.length) {
        await remove(organizationId, admin.id)
          .then(response => {
            if (response instanceof Error) {
              notifyError({
                header: t("delete.error.message", { admin: admin.displayName }),
                "data-testid": "delete-admin-error-alert"
              });
            } else
              notifySuccess({
                header: t("delete.success.message", {
                  admin: admin.displayName
                }),
                "data-testid": "delete-admin-success-alert"
              });
          })
          .catch(() => {
            notifyError({
              header: t("delete.error.message", { admin: admin.displayName }),
              "data-testid": "delete-admin-error-alert"
            });
          });
        index++;
      }
    }
  };

  return (
    <Tooltip message={t("delete.tooltip")}>
      <IconButton
        onClick={handleDelete}
        data-testid={`delete-icon-button`}
        disabled={disabled}
      >
        <TrashIcon className="h-3 sm:h-4" />
      </IconButton>
    </Tooltip>
  );
};

export default RemoveOrganizationAdminButton;
